import history from '../history';
import { resultTypes, initListLengths } from '../appSettings';
import { getMonthFromYearMonthString, getYearFromYearMonthString } from './date';

const list = 'Vacation Finder List';

export const logToDataLayer = (data) => {
  if (!window.dataLayer) {
    window.dataLayer = [];
  }

  window.dataLayer.push({
    ...data,
    eventCategory: 'Vacation Finder',
  });
};

export const getDestinationDetailsEventData = (filters, destinationTraits, destinationTraitNames) => {
  switch (filters.selectedPreference) {
    case destinationTraits.LOCATION:
      return {
        eventAction: destinationTraitNames[destinationTraits.LOCATION],
        eventLabel: filters.selectedLocations,
      };
    case destinationTraits.FLIGHT_TIME:
      return {
        eventAction: destinationTraitNames[destinationTraits.FLIGHT_TIME],
        eventLabel: filters.selectedFlightTime,
      };
    case destinationTraits.TEMPERATURE:
      return {
        eventAction: destinationTraitNames[destinationTraits.TEMPERATURE],
        eventLabel: `${filters.selectedTemperaturePeriod}, ${filters.selectedTemperature}`,
      };
    default:
      return {
        eventAction: '',
        eventLabel: '',
      };
  }
  
}

export const logEventToDataLayer = ({
  filterSteps,
  filterStepsTypes,
  filters,
  activeFilterStep,
  destinationTraits,
  destinationTraitNames,
  eventActionClickType,
}) => {
  let eventAction = '';
  let eventLabel = null;
  switch (filterSteps[activeFilterStep.index].id) {
    case filterStepsTypes.PRIMARY_CLASSIFICATIONS:
      eventAction = filterSteps[activeFilterStep.index].name;
      eventLabel = filters.selectedPrimaryClassifications;
      break;
    case filterStepsTypes.SECONDARY_CLASSIFICATIONS:
      eventAction = filterSteps[activeFilterStep.index].name;
      eventLabel = filters.selectedSecondaryClassifications;
      break;
    case filterStepsTypes.HOTEL_PRODUCTS:
      eventAction = filterSteps[activeFilterStep.index].name;
      eventLabel = filters.selectedHotelProducts;
      break;
    case filterStepsTypes.DESTINATION_TRAITS_SELECTION:
      eventAction = filterSteps[activeFilterStep.index].name;
      eventLabel = filters.selectedPreference;
      break;
    case filterStepsTypes.DESTINATION_TRAITS_DETAILS: {
      const destinationDetails = getDestinationDetailsEventData(filters, destinationTraits, destinationTraitNames);
      eventAction = destinationDetails.eventAction;
      eventLabel = destinationDetails.eventLabel;
      break;
    }
    default:
      return null;
  }
  return logToDataLayer({
    event: 'vacationfinder',
    eventAction: `${eventAction} - Click ${eventActionClickType}`,
    eventLabel,
  });
};

export const getHotelProduct = (hotel) => {
  return {
    name: `${hotel.departureCode} - ${hotel.destinationCode}`,
    id: `${hotel.destinationCode}${hotel.hotelCode}`,
    price: hotel.price || '',
    category: hotel.isCharter ? 'CharterPackage' : 'IndependentPackage',
  };
};

export const getDestinationProduct = (destination) => {
  return {
    name: `${destination.name}`,
    id: `${destination.objectID}`,
  };
};

export const getExcursionProduct = (excursion) => {
  return {
    name: `${excursion.heading}`,
    id: `${excursion.objectID}`,
  };
};

export const getGroupTripProduct = (grouptrip) => {
  return {
    name: `${grouptrip.title}`,
    id: `${grouptrip.objectID}`,
  };
};

export const getArticleProduct = (article) => {
  return {
    name: `${article.title}`,
    id: `${article.objectID}`,
  };
};

const getProductImpressions = (productList, type) => {
  const allProducts = [];
  const tempProductList = productList?.filter((x) => x) || [];

  switch (type) {
    case resultTypes.HOTELS:
      tempProductList.forEach((hotel) => {
        allProducts.push({ ...getHotelProduct(hotel), list });
      });
      break;
    case resultTypes.DESTINATIONS:
      tempProductList.forEach((destination) => {
        allProducts.push({ ...getDestinationProduct(destination), list });
      });
      break;
    case resultTypes.EXCURSIONS:
      tempProductList.forEach((excursion) => {
        allProducts.push({ ...getExcursionProduct(excursion), list });
      });
      break;
    case resultTypes.GROUP_TRIPS:
      tempProductList.forEach((grouptrip) => {
        allProducts.push({ ...getGroupTripProduct(grouptrip), list });
      });
      break;
      case resultTypes.ARTICLES:
        tempProductList.forEach((article) => {
          allProducts.push({ ...getArticleProduct(article), list });
        });
        break;
    default:
      break;
  }

  return allProducts;
};

export const logProductImpressionMixedEvent = (
  hotels,
  destinations,
  excursions,
  grouptrips,
  articles,
  resultListMix,
  mixListCount,
  ) => {
  const resultLists = {
    [resultTypes.HOTELS]: hotels,
    [resultTypes.DESTINATIONS]: destinations,
    [resultTypes.EXCURSIONS]: excursions,
    [resultTypes.GROUP_TRIPS]: grouptrips,
    [resultTypes.ARTICLES]: articles,
  };
  const impressions = [];
  for (let i = 0; i < initListLengths[resultTypes.ALL]; i += 1) {
    const allProducts = [];
    const mixIteration = mixListCount-initListLengths[resultTypes.ALL]+i;
    resultListMix.forEach((resultListItem) => {
      const startingIndex = 
        resultListItem.type === resultTypes.GROUP_TRIPS
        || resultListItem.type === resultTypes.ARTICLES
        ? (mixIteration * resultListItem.numberOfItems) - 1
        : (mixIteration * resultListItem.numberOfItems);
      allProducts.push(
        ...getProductImpressions(
          resultLists[resultListItem.type]?.slice(
            startingIndex, startingIndex + resultListItem.numberOfItems,
          ), 
          resultListItem.type,
      ));
    });
    impressions.push(...allProducts);
  }

  if (impressions.length > 0) {
    logToDataLayer({
      event: 'productImpression',
      ecommerce: {
        impressions,
      },
    });
  }
};

export const logProductImpressionEvent = (productList, type) => {
  const impressions = getProductImpressions(productList, type);
  if (impressions.length > 0) {
    logToDataLayer({
      event: 'productImpression',
      ecommerce: {
        impressions,
      },
    });
  }
};

export const getSearchEvent = (
  pax,
  selectedDepartureMonths,
  totalHits,
  ) => {
  let travelDepartureMonth = "NoneChosen";
  let travelDepartureYear = "NoneChosen";

  if (selectedDepartureMonths?.length === 1) {
    travelDepartureMonth = getMonthFromYearMonthString(...selectedDepartureMonths);
    travelDepartureYear = getYearFromYearMonthString(...selectedDepartureMonths);
  }
  if (selectedDepartureMonths?.length > 1) {
    travelDepartureMonth = "MultipleSelections";
    travelDepartureYear = "MultipleSelections";
  }
  return {
    event: 'Search',
    travelAction: 'Search',
    travelAdults: pax?.selectedNumberOfAdults || 2,
    travelChildren: pax?.selectedNumberOfChildren || 0,
    travelCountryCode: undefined,
    travelDepartureCode: pax?.selectedDeparture,
    travelDepartureDay: "NoneChosen",
    travelDepartureMonth,
    travelDepartureYear,
    travelDestinationCode: "NoneChosen",
    travelDuration: 8,
    travelHits: totalHits,
    travelPax: pax?.selectedNumberOfAdults + pax?.selectedNumberOfChildren || 2,
  };
};

export const logFilterEvent = (
  pax,
  selectedDepartureMonths,
  totalHits,
  travelFilterCategory,
  travelFilterValue,
  ) => {
  logToDataLayer({
    ...getSearchEvent(pax, selectedDepartureMonths, totalHits),
    event: 'Filter',
    travelAction: 'Filter',
    travelFilterSet: 'VacationFinder',
    travelFilterCategory,
    travelFilterValue,
  });
};

export const logProductClickEvent = (trackingProduct) => {
  logToDataLayer({
    event: 'productClick',
    ecommerce: {
      click: {
        actionField: {
          list,
        },
        products: [
          trackingProduct,
        ],
      },
    },
  });
};

// Caller setting second argument (selectedItems) to null to not check if includes in list of selectables
// This is to only trigger FilterEvents upon the the selection and not deselection of an item
export const logFilterEventIfNeeded = (siteSettings, selectedItems, newItem, travelFilterCategory) => {
  if (history.location.pathname?.includes('result') && (!selectedItems?.includes(newItem) || selectedItems === null)) {
    const { pax, selectedDepartureMonths } = siteSettings.filters;
    logFilterEvent(
      pax,
      selectedDepartureMonths,
      siteSettings.totalHits,
      travelFilterCategory,
      newItem,
    );
  }
};